import { JSX, RefObject } from 'react';

import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import { addDays, isWithinInterval } from 'date-fns';

import AddBillingAddressInformation from 'components/payment/components/AddBillingAddressInformation';
import EInvoicePaymentForm from 'components/payment/components/EInvoicePaymentForm';
import FakturaPaymentForm from 'components/payment/components/FakturaPaymentForm';
import StripePaymentContainer from 'components/payment/components/StripePaymentContainer';
import { StripePaymentRef } from 'components/payment/components/StripePaymentContainer/StripePayment';
import VippsPaymentForm from 'components/payment/components/VippsPaymentForm';
import { STRIPE_MINIMUM_PAYMENT_AMOUNT } from 'data/checkout';
import usePaymentFormContext from 'hooks/usePaymentFormContext';
import { OptionalProductAdjustQuantityType } from 'types/productPackage';
import ChoiceInput from 'ui/ChoiceInput';
import FakturaIcon from 'ui/Icon/icons/FakturaIcon';
import NetsIcon from 'ui/Icon/icons/NetsIcon';
import StripeIcon from 'ui/Icon/icons/StripeIcon';
import VippsIcon from 'ui/Icon/icons/VippsIcon';
import { PAYMENT_PROVIDER } from 'utils/GeneratedApi';
import useTenant from 'hooks/useTenant';

type PaymentMethodType = {
  id: string;
  label: string;
  icon: JSX.Element;
};

const PaymentMethods = ({
  shouldDisablePaymentMethod,
  introPrice,
  recurringPrice,
  stripePublishableKey,
  packageId,
  checkoutCompletedUrl,
  reference,
  fullName,
  email,
  slug,
  selectedOptionalProducts,
  introPeriodFixedEndDate,
  handleSetErrorMessage,
  handleSetStripeFromState,
  handleSetPaymentLoading,
  stripePaymentRef,
}: {
  shouldDisablePaymentMethod: boolean;
  introPrice: number;
  recurringPrice: number;
  stripePublishableKey: string;
  packageId: number;
  checkoutCompletedUrl: string;
  reference: string;
  fullName: string;
  email: string;
  slug: string;
  selectedOptionalProducts: OptionalProductAdjustQuantityType[];
  introPeriodFixedEndDate: string;
  handleSetErrorMessage: (message: string) => void;
  handleSetStripeFromState: (stare: 'ready' | 'error' | 'loading') => void;
  handleSetPaymentLoading: (loading: boolean) => void;
  stripePaymentRef: RefObject<StripePaymentRef>;
}) => {
  const tCheckout = useTranslations('checkout');
  const tApiMessageCode = useTranslations('apiMessageCode');

  const tenant = useTenant();

  const { selectedPayementMethod, cardError, paymentProviders, onChangeMethod } =
    usePaymentFormContext();

  const handleChangePaymentMethod = (method: string) => {
    // If the payment method is already selected, do nothing
    if (method !== selectedPayementMethod) {
      onChangeMethod(method);
    }
  };

  const paymentMethods = [
    {
      id: PAYMENT_PROVIDER.Stripe,
      label: tCheckout('card'),
      icon: <StripeIcon />,
    },
    {
      id: PAYMENT_PROVIDER.Vipps,
      label: tCheckout('vipps'),
      icon: <VippsIcon />,
    },
    {
      id: PAYMENT_PROVIDER.Nets,
      label: tCheckout('card'),
      icon: <NetsIcon />,
    },
    {
      id: PAYMENT_PROVIDER.External,
      label: tCheckout('external'),
      icon: <FakturaIcon />,
    },
    {
      id: PAYMENT_PROVIDER.EfakturaAvtalegiroEhf,
      label: tCheckout('efaktura'),
      icon: <FakturaIcon />,
    },
  ];

  const shouldMakeStripePayment =
    introPrice > STRIPE_MINIMUM_PAYMENT_AMOUNT && recurringPrice > STRIPE_MINIMUM_PAYMENT_AMOUNT;

  const filteredPaymentMethods = paymentMethods.filter((method: PaymentMethodType) =>
    paymentProviders.includes(method.id),
  );

  const isIntroPeriodFixedDateEndWithin2Days =
    !!introPeriodFixedEndDate &&
    isWithinInterval(new Date(introPeriodFixedEndDate), {
      start: new Date(),
      end: addDays(new Date(), 2),
    });

  const eInvoicePaymentOrderDaysBeforeRenewal =
    tenant?.preference?.eInvoicePaymentPreference?.orderDaysBeforeRenewal;

  const isIntroPeriodFixedDateEndWithinEInvoicePaymentOrderDaysBeforeRenewal =
    !!eInvoicePaymentOrderDaysBeforeRenewal &&
    !!introPeriodFixedEndDate &&
    isWithinInterval(new Date(introPeriodFixedEndDate), {
      start: new Date(),
      end: addDays(new Date(), eInvoicePaymentOrderDaysBeforeRenewal),
    });

  const shouldDisableByIntroPeriodFixedDateEdgeCase = (paymentProvider: PAYMENT_PROVIDER) => {
    switch (paymentProvider) {
      case PAYMENT_PROVIDER.Vipps:
        return isIntroPeriodFixedDateEndWithin2Days;
      case PAYMENT_PROVIDER.EfakturaAvtalegiroEhf:
        return isIntroPeriodFixedDateEndWithinEInvoicePaymentOrderDaysBeforeRenewal;
      default:
        return false;
    }
  };

  return (
    <div>
      <fieldset className="mt-2">
        <div className="space-y-3">
          {(filteredPaymentMethods || []).map((paymentMethod) => {
            return (
              <div
                key={paymentMethod.id}
                className={classNames(
                  selectedPayementMethod === paymentMethod.id ? 'bg-albaster' : 'bg-white',
                  cardError && 'border border-tiaMaria',
                  'flex flex-col rounded-lg border border-dividerGrey px-4 py-[14px]',
                  shouldDisablePaymentMethod ||
                    shouldDisableByIntroPeriodFixedDateEdgeCase(paymentMethod.id)
                    ? 'cursor-not-allowed'
                    : 'cursor-pointer', // Add a class to visually indicate disabled state
                )}
                onClick={() => {
                  if (
                    !shouldDisablePaymentMethod &&
                    !shouldDisableByIntroPeriodFixedDateEdgeCase(paymentMethod.id)
                  ) {
                    handleChangePaymentMethod(paymentMethod.id);
                  }
                }}
              >
                <div className="w-full flex items-center justify-between">
                  <ChoiceInput
                    id={`payment-method-${paymentMethod.id}`}
                    name={`payment-method-${paymentMethod.id}`}
                    label={paymentMethod.label}
                    checked={selectedPayementMethod === paymentMethod.id}
                    isDisabled={
                      shouldDisablePaymentMethod ||
                      shouldDisableByIntroPeriodFixedDateEdgeCase(paymentMethod.id)
                    }
                    isReadOnly={
                      shouldDisablePaymentMethod ||
                      shouldDisableByIntroPeriodFixedDateEdgeCase(paymentMethod.id)
                    }
                    onChange={() => {}}
                  />
                  <span>{paymentMethod.icon}</span>
                </div>
                {selectedPayementMethod === paymentMethod.id &&
                  paymentMethod.id === PAYMENT_PROVIDER.Stripe && (
                    <div className="mt-4">
                      {stripePublishableKey ? (
                        <>
                          {shouldMakeStripePayment ? (
                            <StripePaymentContainer
                              price={introPrice}
                              stripePaymentRef={stripePaymentRef}
                              stripePublishableKey={stripePublishableKey}
                              packageId={packageId}
                              checkoutCompletedUrl={checkoutCompletedUrl}
                              reference={reference}
                              fullName={fullName}
                              email={email}
                              slug={slug}
                              selectedOptionalProducts={selectedOptionalProducts}
                              handleSetErrorMessage={handleSetErrorMessage}
                              handleSetStripeFromState={handleSetStripeFromState}
                              handleSetPaymentLoading={handleSetPaymentLoading}
                            />
                          ) : (
                            <p className="text-red mt-2">
                              {tCheckout('stripePaymentLimitErrorMessage', {
                                amount: STRIPE_MINIMUM_PAYMENT_AMOUNT,
                              })}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-red mt-2">
                          {tApiMessageCode('paymentProviderConfigNotFound')}
                        </p>
                      )}
                    </div>
                  )}
                {selectedPayementMethod === paymentMethod.id &&
                  paymentMethod.id === PAYMENT_PROVIDER.Vipps && (
                    <>
                      <VippsPaymentForm />
                      <AddBillingAddressInformation />
                    </>
                  )}
                {selectedPayementMethod === paymentMethod.id &&
                  paymentMethod.id === PAYMENT_PROVIDER.Nets && <AddBillingAddressInformation />}
                {selectedPayementMethod === paymentMethod.id &&
                  paymentMethod.id === PAYMENT_PROVIDER.External && <FakturaPaymentForm />}
                {selectedPayementMethod === paymentMethod.id &&
                  paymentMethod.id === PAYMENT_PROVIDER.EfakturaAvtalegiroEhf && (
                    <EInvoicePaymentForm />
                  )}
              </div>
            );
          })}
        </div>
      </fieldset>
    </div>
  );
};

export default PaymentMethods;
